import { useCallback, useEffect, useMemo, useState } from 'react'

import { useGetAssignmentQuery, useGetAssignmentsQuery, useGetCustomerQuery, useGetCustomersQuery, useGetEmployeeQuery, useGetEmployeesQuery } from '../../../api/plamo-api'
import PlamoApiWithSaveNotifications from '../util/with-save-notification'
import { Button, Form, Modal, Row } from 'antd'
import { useNavigate } from 'react-router-dom'

import FormItem, { createRequiredRule } from '../form/form-item';
import dayjs from 'dayjs'
import { AdvancedForm } from '../form/advanced-form'

const { useCreateAssignmentWithSaveNotificationMutation, useUpdateAssignmentWithSaveNotificationMutation } = PlamoApiWithSaveNotifications;

const useGetActiveAndNotExitingEmployeesQuery = (queryArgs, ...rest) => {
  const modifiedQueryArgs = useMemo(() => {
    let filterStr = queryArgs?.f || '{}';
    const filter = JSON.parse(filterStr) as any;
    filter.active = { c: "EQ", v: true };
    filter.dateOfExit = { c: "EQ", v: null };
    filterStr = JSON.stringify(filter);
    return {...(queryArgs) || {}, f: filterStr}
  }, [queryArgs]);
  return useGetEmployeesQuery(modifiedQueryArgs, ...rest);
}

function AssignmentDetails({ id, preselectedCustomerId, preselectedEmployeeId }: any) {
  const [createAssignment] = useCreateAssignmentWithSaveNotificationMutation()
  const [updateAssignment] = useUpdateAssignmentWithSaveNotificationMutation()
  const { data: assignment } = useGetAssignmentQuery({ id }, { skip: id === 'new' }) as { data: any }
  const [form] = Form.useForm();
  const customerId = Form.useWatch("customerId", form)
  const navigate = useNavigate();
  const { data: previousCustomerAssignments } = useGetAssignmentsQuery({ f: JSON.stringify({ "customer.id": { c: 'EQ', v: customerId } }), st: JSON.stringify({ startDate: "ASC", id: "ASC" }) }, { skip: !customerId }) as any
  const lastPreviousCustomerAssignment = useMemo(() => { const items = previousCustomerAssignments?.items || []; return items.length > 0 ? items[items.length - 1] : undefined}, [previousCustomerAssignments])
  const { data: lastPreviousCustomerAssignmentEmployee } = useGetEmployeeQuery({ id: lastPreviousCustomerAssignment?.employeeId }, { skip: !lastPreviousCustomerAssignment?.employeeId }) as any;
  const { data: selectedCustomer } = useGetCustomerQuery({ id: customerId }, { skip: !customerId }) as any;
  const [openUnassignmentDialog, setOpenUnassignmentDialog] = useState<any>(null);

  useEffect(() => {
    form.setFieldsValue(assignment)
  }, [form, assignment])

  const saveNew = useCallback(async (values: any, endPrevious: boolean) => {
    await createAssignment(values);
    if(endPrevious) {
      const updatedPreviousAssignment = {...lastPreviousCustomerAssignment, endDate: dayjs(values.startDate).subtract(1, 'day')}
      await updateAssignment(updatedPreviousAssignment);
    }

    navigate(-1)
  }, [lastPreviousCustomerAssignment, createAssignment, updateAssignment, navigate])
  
  const onFinish = useCallback(async (values: any) => {
    if(id === 'new' && lastPreviousCustomerAssignment) {
      setOpenUnassignmentDialog(values);
    } else if(id === 'new') {
      saveNew(values, false);
    } else {
      const newAssignment = {...assignment, ...values}
      await updateAssignment(newAssignment);
    }
  }, [id, saveNew, updateAssignment, assignment, lastPreviousCustomerAssignment, setOpenUnassignmentDialog]);

  return (<>
       <AdvancedForm form={form} name="assignment" onFinish={onFinish} initialValues={{ employeeId: preselectedEmployeeId, customerId: preselectedCustomerId, startDate: dayjs()}}>
        <Row><h3>Zuordnung</h3></Row>
        <Row gutter={24}>
          <FormItem fieldName="customerId" label="Kunde" readOnly={!!preselectedCustomerId} type="ref" rules={[createRequiredRule('Kunde darf nicht leer sein')]} typeData={{ query: useGetCustomersQuery, getId: customer => customer.id, render: customer => `${customer.firstName} ${customer.lastName}` }} />
          <FormItem fieldName="employeeId" label="Mitarbeiter" readOnly={!!preselectedEmployeeId} type="ref" rules={[createRequiredRule('Mitarbeiter darf nicht leer sein')]} typeData={{ query: useGetActiveAndNotExitingEmployeesQuery, getId: employee => employee.id, render: employee => `${employee.firstName} ${employee.lastName}`}} />
          <FormItem fieldName="startDate" label="Beginn" type="date" rules={[createRequiredRule('Beginn darf nicht leer sein')]} />
          <FormItem fieldName="endDate" label="Ende" type="date" />
        </Row>
      </AdvancedForm>
      <Modal
          title="Vorherige Zuordnung beenden?"
          open={!!openUnassignmentDialog}
          onCancel={() => setOpenUnassignmentDialog(null)}
          footer={(
            <>
              <Button onClick={() => { setOpenUnassignmentDialog(null); }}>Abbrechen</Button>
              <Button onClick={() => { saveNew(openUnassignmentDialog, false); setOpenUnassignmentDialog(null); }}>Nein</Button>
              <Button onClick={() => { saveNew(openUnassignmentDialog, true); setOpenUnassignmentDialog(null); }} type="primary">Ja</Button>
            </>
          )}
          
        >
          <><p>Soll die vorherige Zuordnung <strong>{selectedCustomer?.firstName} {selectedCustomer?.lastName}</strong> &ndash; <strong>{lastPreviousCustomerAssignmentEmployee?.firstName} {lastPreviousCustomerAssignmentEmployee?.lastName}</strong> aufgehoben werden?</p>
          </>
      </Modal>
    </>
  )
}

export default AssignmentDetails